/*
 * Copyright © 2018-2026, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { PageProps } from "../../models/PageProps";
import { Loader } from "../../components/Loader";
import { strings } from "../../common/Strings/Strings";
import { useClinic } from "../../contexts/ClinicContext";
import { CountryDetailsResponse } from "../../models/management/CountryDetailsResponse";
import CountryApi from "../../api/CountryApi";
import logger from "../../util/logger";
import Income from "./Income";
import Usage from "./Usage";
import InventoryStockValue from "./InventoryStockValue";
import UrlTab from "../../components/Tabs/UrlTab";
import NotFound from "../NotFound";
import LocationIndicator from "../../components/Tabs/LocationIndicator";

enum StatisticsTabs {
  income = "income",
  inventoryStock = "inventory-stock",
  softwareUsage = "software-usage",
}

const StatisticsPage = (props: PageProps): ReactElement => {
  const { setPageLoading } = props;
  const [countryDetails, setCountryDetails] = useState<CountryDetailsResponse>();
  const [currentTab, setCurrentTab] = useState<StatisticsTabs>(StatisticsTabs.income);
  const { clinic } = useClinic();

  useEffect(() => {
    const getCountryDetails = async () => {
      if (!clinic) {
        return;
      }

      try {
        const response = await CountryApi.getCountryDetailsOfClinic(clinic.id);
        setCountryDetails(response.data);
      } catch (err) {
        logger.error(err);
      } finally {
        setPageLoading(false);
      }
    };

    void getCountryDetails();
  }, [clinic, setPageLoading]);

  return (
    <main className="main-signed-in">
      <section>
        <div className="flex flex-col items-start md:items-center justify-center space-y-6 mx-auto px-4 lg:px-0 py-10 md:py-6">
          <div>
            <h1 className="flex flex-wrap items-center text-xl leading-tight text-zinc-800 lg:text-2xl dark:text-white">
              <b>{strings.statistics}</b>
              <span className="ml-2">{clinic ? `(${clinic?.name})` : ""}</span>
            </h1>
          </div>
          <div className="border border-gray-200 rounded-md bg-gray-50 !p-4 text-center text-md font-medium text-gray-600 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300">
            <ul className="flex flex-wrap -mb-px">
              <UrlTab
                useBaseUrlOnly
                currentTabKey={currentTab}
                tabKey={StatisticsTabs.income}
                title={strings.incomeSummary}
              />
              <UrlTab
                currentTabKey={currentTab}
                tabKey={StatisticsTabs.inventoryStock}
                title={strings.inventoryStockValue}
              />
              <UrlTab currentTabKey={currentTab} tabKey={StatisticsTabs.softwareUsage} title={strings.softwareUsage} />
            </ul>
          </div>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <LocationIndicator
                    location={StatisticsTabs.income}
                    setLocation={(location: StatisticsTabs) => {
                      setCurrentTab(location);
                    }}
                  />
                  <Income countryDetails={countryDetails} />
                </>
              }
            />
            <Route
              path={`${StatisticsTabs.inventoryStock}`}
              element={
                <>
                  <LocationIndicator
                    location={StatisticsTabs.inventoryStock}
                    setLocation={(location: StatisticsTabs) => {
                      setCurrentTab(location);
                    }}
                  />
                  <InventoryStockValue />
                </>
              }
            />
            <Route
              path={`${StatisticsTabs.softwareUsage}`}
              element={
                <>
                  <LocationIndicator
                    location={StatisticsTabs.softwareUsage}
                    setLocation={(location: StatisticsTabs) => {
                      setCurrentTab(location);
                    }}
                  />
                  <Usage />
                </>
              }
            />
            <Route element={<NotFound />} />
          </Routes>
        </div>
      </section>
    </main>
  );
};

export default Loader(StatisticsPage);
